import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grow,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { DateTimePicker } from '@mui/x-date-pickers';
import { ConversationStatus } from '@provision/conversation-types';
import dayjs from 'dayjs';
import {
  ChangeEvent,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DynamicIcon } from 'src/components';
import { TimeFormats, usePartners, useSnoozeConversation } from 'src/graphql';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { getFormattedDate, getHumanReadableDate } from 'src/utils/date';
import { useArrowNavigation, useDisplayTime, useNotifications } from 'src/utils/hooks';
import {
  ensureFutureDate,
  getDateFromNaturalLanguage,
  resolveNaturalLanguageDate,
} from 'src/utils/natural-language-date';

interface SnoozeItem {
  text: string;
  date?: (timezone?: string) => string;
  onClick?: () => void;
}

const ModalTransition = forwardRef(function ModalTransition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Grow ref={ref} {...props} />;
});

const INITIAL_LIST_ITEMS: SnoozeItem[] = [
  {
    text: 'An hour from now',
    date: () => getDateFromNaturalLanguage('An hour from now'),
  },
  {
    text: 'Tomorrow',
    date: (timezone?: string) =>
      getDateFromNaturalLanguage('Tomorrow at 9am', {
        timezone,
      }),
  },
  {
    text: 'Next week',
    date: () =>
      getDateFromNaturalLanguage('Next week', {
        dayStart: new Date().toLocaleTimeString(),
      }),
  },
  {
    text: 'A month from now',
    date: () =>
      getDateFromNaturalLanguage('A month from now', {
        dayStart: new Date().toLocaleTimeString(),
      }),
  },
];

export const SnoozeConversationModal = () => {
  const [pickerOpen, setPickerOpen] = useState(false);

  const { timeFormat, timeFormatPref } = useDisplayTime();

  const { modalProps } = useModalState(Modals.SnoozeConversationModal);

  const { conversationStatusId, snoozeUntil, partnerId, friendlyId, subject, conversationId } =
    modalProps || {};

  const initialListItems = useMemo(() => {
    const listItems = [...INITIAL_LIST_ITEMS];

    if (
      conversationStatusId !== ConversationStatus.Snoozed &&
      snoozeUntil &&
      dayjs().isBefore(dayjs(snoozeUntil))
    ) {
      listItems.push({
        text: 'Last Snoozed Date',
        date: () => dayjs(snoozeUntil).toISOString(),
      });
    }

    listItems.push({
      text: 'Custom',
      date: () => '',
      onClick: () => {
        setPickerOpen(true);
      },
    });

    return listItems;
  }, [conversationStatusId, snoozeUntil]);

  const { closeModal } = useModalMethods(Modals.SnoozeConversationModal);
  const { isOpened } = useModalState(Modals.SnoozeConversationModal);
  const { snoozeConversation } = useSnoozeConversation();

  const { showErrorAlert, showSuccessAlert } = useNotifications();

  const { partners } = usePartners();

  const currentPartner = partners?.[partnerId ?? ''];

  const [listItems, setListItems] = useState(initialListItems);
  const [input, setInput] = useState('');
  const [customDate, setCustomDate] = useState<dayjs.Dayjs | null>(dayjs());

  const theme = useTheme();

  const closeSnoozeModal = useCallback(() => {
    closeModal();
    setInput('');
  }, [closeModal]);

  useEffect(() => {
    if (isOpened) {
      setListItems(initialListItems);
    }
  }, [initialListItems, isOpened]);

  const submitSnoozeConversation = useCallback(
    async (date?: string, conversationId?: string | null) => {
      if (!date || !conversationId) {
        return;
      }

      const diff = new Date(date).getTime() - new Date().getTime();
      if (diff <= 0) {
        showErrorAlert(
          `Invalid date ${dayjs.duration(diff).humanize()} ago. The snooze date cannot be earlier than or equal to current's date`,
        );

        return;
      }

      closeSnoozeModal();

      const response = await snoozeConversation({
        snoozeUntil: date,
        conversationId,
      });

      if (!response?.success) {
        const genericMessage = 'Something went wrong snoozing the conversation, please try again';
        const err = response?.error ?? '';
        const isValidationOrNotFound =
          err.includes('snoozeUntil') || err.includes('Could not find');
        showErrorAlert(isValidationOrNotFound ? err : genericMessage);
        return;
      }

      showSuccessAlert(`Conversation snoozed for ${dayjs.duration(diff).humanize()}`);
    },
    [closeSnoozeModal, showErrorAlert, showSuccessAlert, snoozeConversation],
  );

  const handleOnItemClick = useCallback(
    async (item: SnoozeItem) => {
      const date = item.date?.(currentPartner?.timeZone);
      await submitSnoozeConversation(date, conversationId);
    },
    [conversationId, currentPartner, submitSnoozeConversation],
  );

  const handleOnChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const { value } = target;
      setInput(value);
      const parsedDateWithDayStart = getDateFromNaturalLanguage(value, {
        dayStart: '9:00am',
      });

      if (!value) {
        setListItems(initialListItems);
      } else if (!parsedDateWithDayStart) {
        setListItems([]);
      } else {
        const parsedDateWithoutDayStart = getDateFromNaturalLanguage(value);
        setListItems([
          {
            text: '',
            date: () =>
              ensureFutureDate(
                resolveNaturalLanguageDate(parsedDateWithDayStart, parsedDateWithoutDayStart),
                value,
              ),
          },
        ]);
      }
    },
    [initialListItems],
  );

  const handleOnDatePickerAccept = useCallback(
    async (value: dayjs.Dayjs | null) => {
      setPickerOpen(false);
      await submitSnoozeConversation(value?.toISOString(), conversationId);
    },
    [conversationId, submitSnoozeConversation],
  );

  const onHandleKeyDown = useArrowNavigation();

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  return conversationId && isOpened ? (
    <Dialog
      open={isOpened}
      onClose={closeSnoozeModal}
      fullWidth
      TransitionComponent={ModalTransition}
    >
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          {!!conversationId && (
            <Chip
              label={`${friendlyId} | ${subject}`}
              sx={{
                borderRadius: theme.spacing(0.5),
                backgroundColor: theme.palette.custom.OV.sys.secondaryContainer,
                minWidth: 0,
              }}
            />
          )}
          <Typography variant="overline">Snooze</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent ref={dialogRef} onKeyDown={(e) => onHandleKeyDown(e, dialogRef)}>
        <TextField
          autoFocus
          placeholder={`Try ${timeFormatPref === TimeFormats.TwelveHour ? '8am' : '20:00'}, in 3 days, Aug 16th`}
          type="text"
          fullWidth
          variant="standard"
          value={input}
          onChange={handleOnChange}
          inputRef={inputRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <DynamicIcon
                  name="Timer"
                  sx={{
                    width: '1.5rem',
                    height: '1.5rem',
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
        <List>
          {listItems.map((item) => (
            <ListItemButton
              key={item.date?.()}
              onClick={() => item.onClick?.() ?? handleOnItemClick(item)}
            >
              <ListItemText
                primary={
                  item.text || getHumanReadableDate(item.date?.(currentPartner?.timeZone))
                }
                sx={{
                  width: 'min-content',
                  '&::first-letter': {
                    textTransform: 'capitalize',
                  },
                }}
              />
              <ListItemText
                secondary={getFormattedDate(item.date?.(currentPartner?.timeZone), timeFormat)}
                sx={{
                  textAlign: 'end',
                  width: 'min-content',
                }}
              />
            </ListItemButton>
          ))}
          <DateTimePicker
            label="Custom time picker"
            disablePast
            open={pickerOpen}
            value={customDate}
            onChange={(newValue) => setCustomDate(newValue)}
            closeOnSelect={false}
            onOpen={() => setPickerOpen(true)}
            onClose={() => {
              setPickerOpen(false);
              setCustomDate(dayjs());
            }}
            slotProps={{
              actionBar: {
                onAccept: async () => {
                  await handleOnDatePickerAccept(customDate);
                  setCustomDate(dayjs());
                },
              },
              popper: {
                sx: {
                  '& .MuiMultiSectionDigitalClockSection-root': {
                    maxHeight: theme.utils.important('auto'),
                  },

                  '& .MuiMultiSectionDigitalClockSection-root::after': {
                    height: theme.utils.important('auto'),
                  },
                },
              },
            }}
            aria-hidden="true"
            sx={{
              visibility: 'hidden',
              position: 'absolute',
              opacity: '0',
              bottom: '0',
              zIndex: '-1',
            }}
          />
        </List>
      </DialogContent>
    </Dialog>
  ) : null;
};

export default memo(SnoozeConversationModal);
